import React from "react";
import ContractDashboard, {
  fromWei,
  formatDuration,
  formatTimestamp,
} from "./shared/ContractDashboard";
import topAchieverFunctions from "./functions/topAchieverFunctions";
import { oxyTopAchiever, oxyTopAchieverAbi } from "./Contract";

/**
 * Oxy Top Achiever — per-cycle buyer leaderboard and the payout pool for the top 5.
 * Every function of the oxyLeader contract is available through the tabs below.
 */
export default function TopAchieverPage() {
  const overview = [
    {
      key: "globalCycle",
      label: "Current Cycle",
      icon: "🔄",
      method: "globalCycle",
      color: "#ffaa00",
    },
    {
      key: "lastRedeemedCycle",
      label: "Last Redeemed Cycle",
      icon: "✅",
      method: "lastRedeemedCycle",
      color: "#00ff88",
    },
    {
      key: "globalLastDraw",
      label: "Last Draw",
      icon: "⏱️",
      method: "globalLastDraw",
      format: (v) => formatTimestamp(v),
      color: "#45b7d1",
    },
    {
      key: "globalPeriod",
      label: "Cycle Period",
      icon: "⌛",
      method: "globalPeriod",
      format: (v) => formatDuration(v),
      color: "#6be0d7",
    },
    {
      key: "myVolume",
      label: "Your Total Volume",
      icon: "📊",
      color: "#ff00aa",
      format: (v) => `${v} USDT`,
      compute: async ({ contract, account }) =>
        fromWei(await contract.methods.buyerVolume(account).call()),
    },
    {
      key: "myCycleVolume",
      label: "Your Cycle Volume",
      icon: "🥇",
      color: "#00ccff",
      format: (v) => `${v} USDT`,
      compute: async ({ contract, account }) =>
        fromWei(await contract.methods.buyerVolumeCycle(account).call()),
    },
    {
      key: "currentPool",
      label: "Current Cycle Pool",
      icon: "🏆",
      color: "#ffaa00",
      format: (v) => `${v} USDT`,
      compute: async ({ contract }) => {
        const cycle = await contract.methods.globalCycle().call();
        return fromWei(await contract.methods.buyerPoolByCycle(cycle).call());
      },
    },
    {
      key: "topBuyers",
      label: "Top 5 This Cycle",
      icon: "👑",
      color: "#ff8800",
      compute: async ({ contract }) => {
        const cycle = await contract.methods.globalCycle().call();
        const rows = await contract.methods.getTopBuyers(cycle).call();
        const active = (rows || []).filter(
          (r) => (r.account ?? r[0]) !== "0x0000000000000000000000000000000000000000"
        );
        return active.length ? `${active.length} ranked` : "No entries yet";
      },
    },
    {
      key: "globalMarketPlaceReward",
      label: "Marketplace Reward Pool",
      icon: "🛒",
      method: "globalMarketPlaceReward",
      format: (v) => `${fromWei(v)} USDT`,
      color: "#00ff88",
    },
    {
      key: "globalMatrixReward",
      label: "Matrix Reward Pool",
      icon: "🧬",
      method: "globalMatrixReward",
      format: (v) => `${fromWei(v)} USDT`,
      color: "#45b7d1",
    },
    {
      key: "contractUsdt",
      label: "Contract USDT",
      icon: "🏦",
      color: "#26a17b",
      format: (v) => `${v} USDT`,
      compute: async ({ getTokenContract, address }) =>
        fromWei(await getTokenContract("USDT").methods.balanceOf(address).call()),
    },
    {
      key: "paused",
      label: "Contract Status",
      icon: "⚡",
      method: "paused",
      format: (v) => (v === true || v === "true" ? "PAUSED" : "ACTIVE"),
      color: "#ff6b6b",
    },
  ];

  return (
    <ContractDashboard
      title="Oxy Top Achiever"
      subtitle="Per-cycle buyer leaderboard and the payout pool for the top 5"
      icon="🥇"
      accent="#ffaa00"
      address={oxyTopAchiever}
      abi={oxyTopAchieverAbi}
      functions={topAchieverFunctions}
      overview={overview}
      approvalTokens={["USDT"]}
    />
  );
}
